import { rem, Select } from '@mantine/core';
import { GridApi } from 'ag-grid-community';
import { getKindPretty } from 'components/tables/data/kind';
import { RevisionRow } from 'components/tables/data/revision/types';
import useFilterValue, { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { useEffect, useMemo, useState } from 'react';

/**
 * The key of the filter. Must be equal to the raw `kind` field.
 */
export const FILTER_KEY = 'kind' as const;

/**
 * Parameters of the RevisionFormTypeSelectFilter component.
 */
export interface RevisionFormTypeSelectFilterProps {
  api: GridApi<RevisionRow>;
  onChange?: FilterChangeCallback;
}

/**
 * Floating filter for filtering forms by their kind.
 */
export default function RevisionFormTypeSelectFilter({ api, onChange }: RevisionFormTypeSelectFilterProps) {
  const [value, setValue] = useFilterValue({ api, filterKey: FILTER_KEY, onChange });
  const [formKinds, setFormKinds] = useState<string[]>([]);

  const options = useMemo(() => formKinds.map((kind) => ({ value: kind, label: getKindPretty(kind) })), [formKinds]);

  useEffect(() => {
    /** Finds unique form kinds visible in the table and exposes them as filter options. */
    const findUniqueKinds = () => {
      const uniqueKinds = new Set<string>();

      api.forEachNode(({ displayed, data }) => {
        if (displayed && data?.kind && data.kind !== 'revision') {
          uniqueKinds.add(data.kind);
        }
      });

      setFormKinds(Array.from(uniqueKinds).sort((a, b) => getKindPretty(a).localeCompare(getKindPretty(b))));
    };

    findUniqueKinds();

    api.addEventListener('modelUpdated', findUniqueKinds);
    return () => api.removeEventListener('modelUpdated', findUniqueKinds);
  }, [api]);

  return (
    <Select
      data={options}
      clearable
      searchable
      w="100%"
      size="sm"
      placeholder="Všetky typy formulárov"
      value={value}
      onChange={setValue}
      styles={{ dropdown: { minWidth: rem(250) } }}
    />
  );
}
